import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import UserBadge from './UserBadge';
import { Button } from './ui/button';
import { X } from 'lucide-react';

const TIER_COLORS = {
  platinum: {
    from: 'rgba(148,163,184,0.25)',
    to: 'rgba(30,41,59,0.0)',
    border: 'rgba(148,163,184,0.25)',
    accent: '#94a3b8',
    confetti: ['#94a3b8', '#cbd5e1', '#f1f5f9', '#475569'],
  },
  diamond: {
    from: 'rgba(59,130,246,0.3)',
    to: 'rgba(15,23,42,0.0)',
    border: 'rgba(59,130,246,0.3)',
    accent: '#3b82f6',
    confetti: ['#3b82f6', '#0ea5e9', '#a5f3fc', '#1e3a8a', '#93c5fd'],
  },
  gold: {
    from: 'rgba(245,158,11,0.28)',
    to: 'rgba(15,23,42,0.0)',
    border: 'rgba(245,158,11,0.3)',
    accent: '#f59e0b',
    confetti: ['#f59e0b', '#fbbf24', '#fde68a', '#d97706'],
  },
  bronze: {
    from: 'rgba(180,83,9,0.22)',
    to: 'rgba(15,23,42,0.0)',
    border: 'rgba(180,83,9,0.25)',
    accent: '#b45309',
    confetti: ['#b45309', '#c2410c', '#fdba74', '#92400e'],
  },
};

export default function BadgeCelebration({ badge, onClose, show = true }) {
  const [stage, setStage] = useState('intro');

  useEffect(() => {
    if (!show) return;

    const colors = TIER_COLORS[badge?.tier]?.confetti || ['#FFD700', '#FFC700'];
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.6 }, colors });
      confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.6 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();

    const t1 = setTimeout(() => setStage('badge-reveal'), 1000);
    const t2 = setTimeout(() => setStage('thank-you'), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [show, badge?.tier]);

  if (!show) return null;

  const cfg = TIER_COLORS[badge?.tier] || TIER_COLORS.gold;

  const msg = (() => {
    switch (badge?.tier) {
      case 'platinum': return {
        title: 'Platinum Supporter!',
        subtitle: 'You are a true legend.',
        message: 'Your generous support and premium membership mean the world to us. Thank you for being an incredible part of our community!',
      };
      case 'diamond': return {
        title: 'Diamond Member!',
        subtitle: 'Welcome to Premium.',
        message: 'You now have full access to all Pro features. Thank you for supporting TheMoneyMap!',
      };
      case 'gold': return {
        title: 'Gold Supporter!',
        subtitle: 'Thank you for your generosity.',
        message: 'Your support helps us build better features and keep TheMoneyMap running. You rock!',
      };
      default: return {
        title: 'Welcome!',
        subtitle: 'Thanks for being here.',
        message: 'Start your financial journey with TheMoneyMap.',
      };
    }
  })();

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white/70 hover:text-white hover:bg-white/10 z-10"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>

          <div className="relative max-w-md w-full mx-4">
            <motion.div
              className="relative rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e2d5a 50%, #0f172a 100%)',
                border: `1px solid ${cfg.border}`,
                boxShadow: `0 0 60px ${cfg.from}, 0 0 0 1px ${cfg.border}, 0 25px 50px rgba(0,0,0,0.5)`,
              }}
              initial={{ scale: 0.8, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 220, damping: 22 }}
            >
              {/* Radial glow top */}
              <div
                className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(circle, ${cfg.from} 0%, transparent 70%)` }}
              />

              {/* Floating dots */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 rounded-full"
                    style={{
                      left: `${10 + (i * 7.5) % 85}%`,
                      top: `${15 + (i * 11) % 75}%`,
                      backgroundColor: cfg.accent,
                    }}
                    animate={{ opacity: [0, 0.6, 0], scale: [0, 1.5, 0] }}
                    transition={{
                      duration: 2.5,
                      repeat: Infinity,
                      delay: i * 0.18,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </div>

              {/* Content */}
              <div className="relative z-10 p-10 text-center">
                <AnimatePresence mode="wait">
                  {stage === 'intro' && (
                    <motion.div
                      key="intro"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="space-y-4 py-4"
                    >
                      <motion.div
                        className="text-6xl"
                        animate={{ rotate: [0, 12, -12, 8, 0], scale: [1, 1.15, 1] }}
                        transition={{ duration: 0.6 }}
                      >
                        {badge?.tier === 'diamond' ? '💎' : badge?.tier === 'platinum' ? '✨' : '🎉'}
                      </motion.div>
                      <h2
                        className="text-3xl font-bold"
                        style={{
                          background: `linear-gradient(90deg, ${cfg.accent}, #fff, ${cfg.accent})`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        Amazing!
                      </h2>
                    </motion.div>
                  )}

                  {stage === 'badge-reveal' && (
                    <motion.div
                      key="badge"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-5 py-4"
                    >
                      <div className="flex justify-center">
                        <UserBadge badge={badge} size="xl" animate />
                      </div>
                      <motion.h2
                        className="text-2xl font-bold text-white"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        {msg.title}
                      </motion.h2>
                    </motion.div>
                  )}

                  {stage === 'thank-you' && (
                    <motion.div
                      key="thanks"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-6 py-2"
                    >
                      <div className="flex justify-center">
                        <UserBadge badge={badge} size="xl" showName />
                      </div>

                      <div className="space-y-2">
                        <h2 className="text-xl font-bold text-white">{msg.subtitle}</h2>
                        <p className="text-sm leading-relaxed" style={{ color: 'rgba(147,197,253,0.65)' }}>
                          {msg.message}
                        </p>
                      </div>

                      <motion.button
                        onClick={onClose}
                        className="w-full py-3 px-6 rounded-xl font-semibold text-white"
                        style={{
                          background: `linear-gradient(135deg, #1d4ed8, #3b82f6, #0ea5e9)`,
                          boxShadow: '0 0 25px rgba(59,130,246,0.4)',
                        }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.3, type: 'spring' }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Continue to Dashboard
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
